import { useCallback, useEffect, useMemo, useState } from 'react';
import { addFavoritePB, isLoggedIn, loadFavoritesLS, mergeLocalWithPB, removeFavoritePB, removeAllFavoritesPB, saveFavoritesLS } from '../lib/favorites';
import { pb } from '../lib/pb';

let lastSetOpen = null;

function createSnapshotFromProduct(p) {
  if (!p) return null;
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    images: Array.isArray(p.images) ? p.images : [],
    collectionId: p.collectionId,
    collectionName: p.collectionName || 'products'
  };
}

export function useFavorites(opts = {}) {
  const registerOpener = !!opts.registerOpener;
  const [items, setItems] = useState(() => loadFavoritesLS());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const merged = await mergeLocalWithPB();
        if (mounted) setItems(merged);
      } catch { }
    })();
    const onStorage = (e) => {
      if (e.key === 'favorites.products') {
        const next = loadFavoritesLS();
        setItems(next);
      }
    };
    const onCustom = () => {
      const next = loadFavoritesLS();
      setItems(next);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('favorites:updated', onCustom);
    return () => { mounted = false; window.removeEventListener('storage', onStorage); window.removeEventListener('favorites:updated', onCustom); };
  }, []);

  // React to authentication changes (e.g., user logs in):
  // re-merge local and server favorites and broadcast update so all consumers refresh (e.g., header badge)
  useEffect(() => {
    const store = pb?.authStore;
    if (!store || typeof store.onChange !== 'function') return;
    const unsub = store.onChange(async () => {
      try {
        const merged = await mergeLocalWithPB();
        setItems(merged);
        try { window.dispatchEvent(new Event('favorites:updated')); } catch { }
      } catch { }
    });
    return () => { try { if (typeof unsub === 'function') unsub(); } catch { } };
  }, []);

  const findIndex = (id) => items.findIndex(x => x.id === id);
  const isFavorite = (id) => findIndex(id) >= 0;

  const add = async (productOrSnapshot) => {
    const snap = productOrSnapshot?.id && productOrSnapshot.slug ? productOrSnapshot : createSnapshotFromProduct(productOrSnapshot);
    if (!snap?.id) return;
    setItems(prev => {
      if (prev.some(x => x.id === snap.id)) return prev;
      const next = [snap, ...prev];
      saveFavoritesLS(next);
      try { window.dispatchEvent(new Event('favorites:updated')); } catch { }
      return next;
    });
    if (isLoggedIn()) {
      try { await addFavoritePB(snap.id); } catch { }
    }
  };

  const remove = async (id) => {
    setItems(prev => {
      const next = prev.filter(x => x.id !== id);
      saveFavoritesLS(next);
      try { window.dispatchEvent(new Event('favorites:updated')); } catch { }
      return next;
    });
    if (isLoggedIn()) {
      try { await removeFavoritePB(id); } catch { }
    }
  };

  const toggle = async (productOrSnapshot) => {
    const id = productOrSnapshot?.id;
    if (!id) return;
    if (isFavorite(id)) await remove(id); else await add(productOrSnapshot);
  };

  const clearAll = async () => {
    setItems(() => {
      saveFavoritesLS([]);
      try { window.dispatchEvent(new Event('favorites:updated')); } catch { }
      return [];
    });
    if (isLoggedIn()) {
      try { await removeAllFavoritesPB(); } catch { }
    }
  };

  const value = useMemo(() => ({ items, count: items.length, open, setOpen, add, remove, toggle, isFavorite, clearAll }), [items, open]);

  if (registerOpener) {
    lastSetOpen = setOpen;
  }
  return value;
}

export function openFavoritesDrawer() { if (lastSetOpen) lastSetOpen(true); }
export function closeFavoritesDrawer() { if (lastSetOpen) lastSetOpen(false); }
